import imageCompression from 'browser-image-compression'
import heic2any from 'heic2any'
import exifr from 'exifr'

const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'application/pdf',
]

const ALLOWED_VIDEO_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
]

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

export function validateFile(file, type) {
  if (!file) {
    return { valid: false, error: 'No file selected.' }
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File is too large (${formatFileSize(file.size)}). Maximum size is 10MB.`,
    }
  }

  const allowed =
    type === 'video'
      ? ALLOWED_VIDEO_TYPES
      : ALLOWED_IMAGE_TYPES

  if (!allowed.includes(file.type) && !isHeicByExtension(file.name)) {
    return {
      valid: false,
      error: `File type not supported. Accepted: ${allowed.map((t) => t.split('/')[1].toUpperCase()).join(', ')}.`,
    }
  }

  return { valid: true }
}

function isHeicByExtension(name) {
  const ext = name.split('.').pop()?.toLowerCase()
  return ext === 'heic' || ext === 'heif'
}

export async function processFile(file, type) {
  let processed = file

  if (isHeicByExtension(file.name) || file.type === 'image/heic' || file.type === 'image/heif') {
    processed = await convertHeicToJpeg(file)
  }

  if (type !== 'video' && processed.type.startsWith('image/')) {
    processed = await compressImage(processed)
    processed = await autoRotateImage(processed)
  }

  return processed
}

async function convertHeicToJpeg(file) {
  try {
    const blob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.92,
    })
    return new File([blob], file.name.replace(/\.heic$/i, '.jpg'), {
      type: 'image/jpeg',
    })
  } catch (err) {
    throw new Error('Failed to convert HEIC image. Please try a different file.')
  }
}

async function compressImage(file) {
  try {
    const options = {
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: 'image/jpeg',
      initialQuality: 0.92,
    }
    return await imageCompression(file, options)
  } catch (err) {
    return file
  }
}

async function autoRotateImage(file) {
  try {
    const orientation = await exifr.orientation(file)
    if (!orientation || orientation === 1) return file

    const img = await loadImage(URL.createObjectURL(file))
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    const { width, height } = getRotatedDimensions(orientation, img.width, img.height)
    canvas.width = width
    canvas.height = height

    applyOrientation(ctx, orientation, width, height)
    ctx.drawImage(img, 0, 0)

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.92)
    )
    return new File([blob], file.name, { type: 'image/jpeg' })
  } catch (err) {
    return file
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

function getRotatedDimensions(orientation, w, h) {
  switch (orientation) {
    case 5:
    case 6:
    case 7:
    case 8:
      return { width: h, height: w }
    default:
      return { width: w, height: h }
  }
}

function applyOrientation(ctx, orientation, w, h) {
  switch (orientation) {
    case 2:
      ctx.translate(w, 0)
      ctx.scale(-1, 1)
      break
    case 3:
      ctx.translate(w, h)
      ctx.rotate(Math.PI)
      break
    case 4:
      ctx.translate(0, h)
      ctx.scale(1, -1)
      break
    case 5:
      ctx.translate(h, 0)
      ctx.rotate(Math.PI / 2)
      ctx.scale(1, -1)
      break
    case 6:
      ctx.translate(h, 0)
      ctx.rotate(Math.PI / 2)
      break
    case 7:
      ctx.translate(0, w)
      ctx.rotate(-Math.PI / 2)
      ctx.scale(1, -1)
      break
    case 8:
      ctx.translate(0, w)
      ctx.rotate(-Math.PI / 2)
      break
  }
}

export function formatFileSize(bytes) {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}
